/**
 * Support Admin Dashboard
 *
 * Admin interface for viewing and managing support chat conversations.
 * Requires SUPPORT_ADMIN_KEY to access.
 */

import { useState, useEffect, useCallback } from 'react';
import { Icons } from '../components/Icons';
import { StorageService } from '../services/storage';

// Types
interface SupportConversation {
  id: string;
  session_id: string;
  user_id: string | null;
  context: {
    page?: string;
    userAgent?: string;
    mode?: string;
    errors?: string[];
    warnings?: string[];
    logs?: string[];
    screenSize?: { width: number; height: number };
    localStorage?: Record<string, string>;
  };
  status: 'active' | 'resolved' | 'escalated' | 'archived';
  priority: 'low' | 'normal' | 'high' | 'critical';
  category: string | null;
  summary: string | null;
  email_sent: boolean;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  last_message_at: string;
  messages?: { count: number }[];
  emails?: { count: number }[];
}

interface SupportMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata: {
    screenshot?: string;
    hasScreenshot?: boolean;
    screenSize?: { width: number; height: number };
    errors?: string[];
    warnings?: string[];
    logs?: string[];
    localStorage?: Record<string, string>;
    provider?: string;
    tokens_input?: number;
    tokens_output?: number;
    email_triggered?: boolean;
  };
  created_at: string;
}

interface SupportEmail {
  id: string;
  email_type: string;
  priority: string;
  subject: string;
  status: string;
  created_at: string;
}

interface SupportStats {
  total_conversations: number;
  active_conversations: number;
  resolved_conversations: number;
  escalated_conversations: number;
  total_messages: number;
  emails_sent: number;
  conversations_today: number;
  conversations_this_week: number;
  by_category: Record<string, number>;
  by_priority: Record<string, number>;
}

const STATUS_COLORS = {
  active: '#10b981',
  resolved: '#6b7280',
  escalated: '#f59e0b',
  archived: '#374151',
};

const PRIORITY_COLORS = {
  low: '#10b981',
  normal: '#3b82f6',
  high: '#f59e0b',
  critical: '#ef4444',
};

interface SupportAdminProps {
  adminKey?: string; // Optional: if provided, skip login screen
}

export function SupportAdmin({ adminKey: propAdminKey }: SupportAdminProps = {}) {
  // Auth state
  const [adminKey, setAdminKey] = useState(() =>
    propAdminKey || StorageService.get<string>('support_admin_key', '')
  );
  const [isAuthenticated, setIsAuthenticated] = useState(!!propAdminKey);
  const [authError, setAuthError] = useState<string | null>(null);

  // Data state
  const [stats, setStats] = useState<SupportStats | null>(null);
  const [conversations, setConversations] = useState<SupportConversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<SupportConversation | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [emails, setEmails] = useState<SupportEmail[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewingScreenshot, setViewingScreenshot] = useState<string | null>(null);
  const [viewingDebugInfo, setViewingDebugInfo] = useState<SupportMessage | null>(null);

  // API helper
  const apiCall = useCallback(async (action: string, params: Record<string, string> = {}, options: RequestInit = {}) => {
    const queryParams = new URLSearchParams({ action, ...params });
    const response = await fetch(`/api/support/admin?${queryParams}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminKey}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        setIsAuthenticated(false);
        throw new Error('Non autorisé');
      }
      const error = await response.json();
      throw new Error(error.error || 'Erreur API');
    }

    return response.json();
  }, [adminKey]);

  // Login handler
  const handleLogin = async () => {
    setLoading(true);
    setAuthError(null);

    try {
      await apiCall('stats');
      StorageService.set('support_admin_key', adminKey);
      setIsAuthenticated(true);
    } catch {
      setAuthError('Clé admin invalide');
    } finally {
      setLoading(false);
    }
  };

  // Load stats and conversations
  const loadData = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    try {
      const [statsData, convsData] = await Promise.all([
        apiCall('stats'),
        apiCall('conversations', {
          status: statusFilter,
          limit: '50',
          ...(searchQuery && { search: searchQuery }),
        }),
      ]);

      setStats(statsData);
      setConversations(convsData.conversations || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, apiCall, statusFilter, searchQuery]);

  // Load conversation details
  const loadConversation = async (id: string) => {
    setLoading(true);
    try {
      const data = await apiCall('conversation', { id });
      setSelectedConversation(data.conversation);
      setMessages(data.messages);
      setEmails(data.emails);
    } catch (error) {
      console.error('Error loading conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update conversation
  const updateConversation = async (id: string, updates: Record<string, unknown>) => {
    try {
      await apiCall('update', { id }, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      await loadData();
      if (selectedConversation?.id === id) {
        await loadConversation(id);
      }
    } catch (error) {
      console.error('Error updating conversation:', error);
    }
  };

  // Auto-load on auth
  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, loadData]);

  // Try auto-login with stored key
  useEffect(() => {
    if (adminKey && !isAuthenticated) {
      handleLogin();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--bg-primary)',
        padding: '20px',
      }}>
        <div style={{
          background: 'var(--card-bg)',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '400px',
          width: '100%',
          border: '1px solid var(--border)',
        }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <Icons.Shield size={48} style={{ color: '#6366f1', marginBottom: '16px' }} />
            <h1 style={{ fontSize: '24px', fontWeight: 600, color: 'var(--text-primary)' }}>
              Support Admin
            </h1>
            <p style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>
              Entrez votre clé admin pour accéder au tableau de bord
            </p>
          </div>

          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Clé admin"
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              background: 'var(--input-bg)',
              color: 'var(--text-primary)',
              fontSize: '14px',
              marginBottom: '16px',
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
          />

          {authError && (
            <p style={{ color: '#ef4444', fontSize: '14px', marginBottom: '16px', textAlign: 'center' }}>
              {authError}
            </p>
          )}

          <button
            onClick={handleLogin}
            disabled={loading || !adminKey}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: 'none',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              cursor: loading || !adminKey ? 'not-allowed' : 'pointer',
              opacity: loading || !adminKey ? 0.6 : 1,
            }}
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>
            Support Admin
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            Gestion des conversations support
          </p>
        </div>
        <button
          onClick={() => {
            setIsAuthenticated(false);
            StorageService.remove('support_admin_key');
          }}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: '1px solid var(--border)',
            background: 'transparent',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >
          Déconnexion
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '16px',
          marginBottom: '24px',
        }}>
          <StatCard title="Total" value={stats.total_conversations} icon={<Icons.MessageSquare size={20} />} />
          <StatCard title="Actives" value={stats.active_conversations} icon={<Icons.Clock size={20} />} color="#10b981" />
          <StatCard title="Résolues" value={stats.resolved_conversations} icon={<Icons.CheckCircle size={20} />} color="#6b7280" />
          <StatCard title="Escaladées" value={stats.escalated_conversations} icon={<Icons.AlertTriangle size={20} />} color="#f59e0b" />
          <StatCard title="Messages" value={stats.total_messages} icon={<Icons.Send size={20} />} />
          <StatCard title="Emails" value={stats.emails_sent} icon={<Icons.Mail size={20} />} />
        </div>
      )}

      {/* Main content */}
      <div style={{ display: 'grid', gridTemplateColumns: selectedConversation ? '400px 1fr' : '1fr', gap: '24px' }}>
        {/* Conversations list */}
        <div style={{
          background: 'var(--card-bg)',
          borderRadius: '12px',
          border: '1px solid var(--border)',
          overflow: 'hidden',
        }}>
          {/* Filters */}
          <div style={{ padding: '16px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              {['all', 'active', 'escalated', 'resolved'].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    background: statusFilter === status ? '#6366f1' : 'var(--hover-bg)',
                    color: statusFilter === status ? 'white' : 'var(--text-secondary)',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  {status === 'all' ? 'Toutes' : status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher..."
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                background: 'var(--input-bg)',
                color: 'var(--text-primary)',
                fontSize: '13px',
              }}
            />
          </div>

          {/* List */}
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {loading && conversations.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                Chargement...
              </div>
            ) : conversations.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                Aucune conversation
              </div>
            ) : (
              conversations.map((conv) => (
                <div
                  key={conv.id}
                  onClick={() => loadConversation(conv.id)}
                  style={{
                    padding: '16px',
                    borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                    background: selectedConversation?.id === conv.id ? 'var(--hover-bg)' : 'transparent',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      background: STATUS_COLORS[conv.status] + '20',
                      color: STATUS_COLORS[conv.status],
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                    }}>
                      {conv.status}
                    </span>
                    <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
                      {formatDate(conv.last_message_at)}
                    </span>
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                    {conv.summary || conv.session_id.slice(0, 20)}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    <span>{conv.messages?.[0]?.count || 0} messages</span>
                    {conv.email_sent && <span style={{ color: '#10b981' }}>✉️ Email envoyé</span>}
                    {conv.priority !== 'normal' && (
                      <span style={{ color: PRIORITY_COLORS[conv.priority] }}>
                        ● {conv.priority}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Conversation detail */}
        {selectedConversation && (
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            border: '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100vh - 200px)',
          }}>
            {/* Header */}
            <div style={{ padding: '16px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <h2 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Conversation
                </h2>
                <button
                  onClick={() => setSelectedConversation(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-secondary)',
                  }}
                >
                  <Icons.X size={20} />
                </button>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <select
                  aria-label="Conversation status"
                  value={selectedConversation.status}
                  onChange={(e) => updateConversation(selectedConversation.id, { status: e.target.value })}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: '1px solid var(--border)',
                    background: 'var(--input-bg)',
                    color: 'var(--text-primary)',
                    fontSize: '13px',
                  }}
                >
                  <option value="active">Active</option>
                  <option value="resolved">Résolue</option>
                  <option value="escalated">Escaladée</option>
                  <option value="archived">Archivée</option>
                </select>

                <select
                  aria-label="Conversation priority"
                  value={selectedConversation.priority}
                  onChange={(e) => updateConversation(selectedConversation.id, { priority: e.target.value })}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: '1px solid var(--border)',
                    background: 'var(--input-bg)',
                    color: 'var(--text-primary)',
                    fontSize: '13px',
                  }}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              {/* Context */}
              <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                <div>Page: {selectedConversation.context?.page || 'N/A'}</div>
                <div>Mode: {selectedConversation.context?.mode || 'N/A'}</div>
                <div>User ID: {selectedConversation.user_id || 'Anonyme'}</div>
              </div>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  style={{
                    marginBottom: '12px',
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <div style={{
                    maxWidth: '80%',
                    padding: '10px 14px',
                    borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                    background: msg.role === 'user' ? '#6366f1' : 'var(--hover-bg)',
                    color: msg.role === 'user' ? 'white' : 'var(--text-primary)',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap',
                  }}>
                    <div style={{ marginBottom: '4px', opacity: 0.7, fontSize: '11px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{msg.role === 'user' ? '👤 Utilisateur' : '🤖 Assistant'} • {formatDate(msg.created_at)}</span>
                      {msg.metadata?.provider && (
                        <span style={{ opacity: 0.6 }}>via {msg.metadata.provider}</span>
                      )}
                    </div>

                    {/* Screenshot */}
                    {msg.metadata?.screenshot && (
                      <div style={{ marginBottom: '8px' }}>
                        <img
                          src={msg.metadata.screenshot}
                          alt="Screenshot"
                          onClick={() => setViewingScreenshot(msg.metadata.screenshot || null)}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '200px',
                            borderRadius: '8px',
                            border: '2px solid rgba(255,255,255,0.3)',
                            cursor: 'pointer',
                          }}
                        />
                      </div>
                    )}

                    {msg.content}

                    {/* Debug info button for user messages */}
                    {msg.role === 'user' && (msg.metadata?.errors?.length || msg.metadata?.warnings?.length || msg.metadata?.logs?.length) && (
                      <button
                        onClick={() => setViewingDebugInfo(msg)}
                        style={{
                          marginTop: '8px',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: 'none',
                          background: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          fontSize: '11px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <Icons.Bug size={12} />
                        Debug Info ({(msg.metadata?.errors?.length || 0) + (msg.metadata?.warnings?.length || 0) + (msg.metadata?.logs?.length || 0)})
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Emails log */}
            {emails.length > 0 && (
              <div style={{ padding: '16px', borderTop: '1px solid var(--border)' }}>
                <h3 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)' }}>
                  Emails envoyés ({emails.length})
                </h3>
                {emails.map((email) => (
                  <div key={email.id} style={{
                    padding: '8px',
                    background: 'var(--hover-bg)',
                    borderRadius: '6px',
                    fontSize: '12px',
                    marginBottom: '4px',
                  }}>
                    <span style={{ color: email.status === 'sent' ? '#10b981' : '#ef4444' }}>
                      {email.status === 'sent' ? '✓' : '✗'}
                    </span>
                    {' '}{email.subject}
                    <span style={{ color: 'var(--text-tertiary)', marginLeft: '8px' }}>
                      {formatDate(email.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Screenshot Modal */}
      {viewingScreenshot && (
        <div
          onClick={() => setViewingScreenshot(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.9)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            cursor: 'zoom-out',
          }}
        >
          <img
            src={viewingScreenshot}
            alt="Screenshot"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              borderRadius: '8px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
            }}
          />
          <button
            onClick={() => setViewingScreenshot(null)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icons.X size={24} />
          </button>
        </div>
      )}

      {/* Debug Info Modal */}
      {viewingDebugInfo && (
        <div
          onClick={() => setViewingDebugInfo(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card-bg)',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '800px',
              maxHeight: '80vh',
              width: '100%',
              overflowY: 'auto',
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-primary)' }}>
                Debug Information
              </h3>
              <button
                onClick={() => setViewingDebugInfo(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                }}
              >
                <Icons.X size={20} />
              </button>
            </div>

            {/* Screen Size */}
            {viewingDebugInfo.metadata?.screenSize && (
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  Screen Size
                </h3>
                <div style={{
                  padding: '8px 12px',
                  background: 'var(--hover-bg)',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: 'var(--text-primary)',
                }}>
                  {viewingDebugInfo.metadata.screenSize.width} × {viewingDebugInfo.metadata.screenSize.height}
                </div>
              </div>
            )}

            {/* Console Errors */}
            {viewingDebugInfo.metadata?.errors?.length ? (
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#ef4444', marginBottom: '8px' }}>
                  Console Errors ({viewingDebugInfo.metadata.errors.length})
                </h3>
                <pre style={{
                  padding: '12px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  borderRadius: '6px',
                  fontSize: '11px',
                  color: '#ef4444',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                }}>
                  {viewingDebugInfo.metadata.errors.join('\n')}
                </pre>
              </div>
            ) : null}

            {/* Console Warnings */}
            {viewingDebugInfo.metadata?.warnings?.length ? (
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#f59e0b', marginBottom: '8px' }}>
                  Console Warnings ({viewingDebugInfo.metadata.warnings.length})
                </h3>
                <pre style={{
                  padding: '12px',
                  background: 'rgba(245, 158, 11, 0.1)',
                  borderRadius: '6px',
                  fontSize: '11px',
                  color: '#f59e0b',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                }}>
                  {viewingDebugInfo.metadata.warnings.join('\n')}
                </pre>
              </div>
            ) : null}

            {/* Console Logs */}
            {viewingDebugInfo.metadata?.logs?.length ? (
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  Console Logs ({viewingDebugInfo.metadata.logs.length})
                </h3>
                <pre style={{
                  padding: '12px',
                  background: 'var(--hover-bg)',
                  borderRadius: '6px',
                  fontSize: '11px',
                  color: 'var(--text-primary)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid var(--border)',
                }}>
                  {viewingDebugInfo.metadata.logs.join('\n')}
                </pre>
              </div>
            ) : null}

            {/* LocalStorage */}
            {viewingDebugInfo.metadata?.localStorage && Object.keys(viewingDebugInfo.metadata.localStorage).length > 0 ? (
              <div>
                <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#3b82f6', marginBottom: '8px' }}>
                  LocalStorage ({Object.keys(viewingDebugInfo.metadata.localStorage).length} keys)
                </h3>
                <pre style={{
                  padding: '12px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '6px',
                  fontSize: '11px',
                  color: '#3b82f6',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                }}>
                  {JSON.stringify(viewingDebugInfo.metadata.localStorage, null, 2)}
                </pre>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

// Stat card component
function StatCard({ title, value, icon, color }: { title: string; value: number; icon: React.ReactNode; color?: string }) {
  return (
    <div style={{
      background: 'var(--card-bg)',
      borderRadius: '12px',
      padding: '16px',
      border: '1px solid var(--border)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '8px',
          background: (color || '#6366f1') + '20',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color || '#6366f1',
        }}>
          {icon}
        </div>
        <div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--text-primary)' }}>
            {value}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            {title}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SupportAdmin;
